import React from 'react';
import LegalDoc from '../../components/LegalDoc';

export default function CapacityPlanning() {
  return (
    <LegalDoc
      slug="capacity-planning"
      title="Capacity Planning Runbook"
      subtitle="Triggers for scaling MongoDB, APScheduler, and the FastAPI/React surface — sized for honest growth."
      version="1.0"
      lastReviewed="2026-02-18"
      controls={["A1.2"]}
    >
      <h2>Current capacity (Feb 2026)</h2>
      <ul>
        <li><strong>FastAPI backend</strong>: single-pod (Kubernetes), 2 vCPU + 4GB RAM, supervisor-managed</li>
        <li><strong>React frontend</strong>: single-pod static-served via nginx, behind the same ingress</li>
        <li><strong>MongoDB Atlas</strong>: M10 dedicated cluster, ~300 GB storage, 3-node replica set</li>
        <li><strong>APScheduler</strong>: in-process AsyncIO scheduler (single instance) running ~12 cron jobs</li>
      </ul>

      <h2>Scaling triggers</h2>
      <p>Each trigger is a measurable threshold + a documented response. We do NOT speculatively over-provision; we DO move when a threshold trips.</p>

      <h3>FastAPI backend</h3>
      <table>
        <thead><tr><th>Metric</th><th>Trigger</th><th>Response</th></tr></thead>
        <tbody>
          <tr><td>p95 request latency</td><td>&gt; 800ms for 24h</td><td>Add horizontal replica (HPA target: 60% CPU)</td></tr>
          <tr><td>CPU utilisation</td><td>&gt; 70% sustained over 4h</td><td>Scale pod RAM/CPU up one tier</td></tr>
          <tr><td>Error rate</td><td>&gt; 0.5% over 1h</td><td>SEV-2 incident; do NOT auto-scale, investigate first</td></tr>
          <tr><td>Concurrent tenants</td><td>&gt; 50 active orgs</td><td>Switch from in-process scheduler to externalized (Redis-backed)</td></tr>
        </tbody>
      </table>

      <h3>MongoDB Atlas</h3>
      <table>
        <thead><tr><th>Metric</th><th>Trigger</th><th>Response</th></tr></thead>
        <tbody>
          <tr><td>Storage used</td><td>&gt; 80% of cluster size</td><td>Upgrade tier (M10 → M20 → M30 → sharded)</td></tr>
          <tr><td>Connections</td><td>&gt; 70% of pool</td><td>Increase Motor pool size; if still hot, increase replica count</td></tr>
          <tr><td>Per-tenant DB count</td><td>&gt; 200 tenant DBs on one cluster</td><td>Move to multi-cluster sharding (router lookup by tenant prefix)</td></tr>
          <tr><td>Replication lag</td><td>&gt; 5 sec on secondaries</td><td>SEV-3 ticket; investigate slow queries; index audit</td></tr>
        </tbody>
      </table>

      <h3>APScheduler / cron</h3>
      <table>
        <thead><tr><th>Metric</th><th>Trigger</th><th>Response</th></tr></thead>
        <tbody>
          <tr><td>Job execution time</td><td>&gt; 50% of interval</td><td>Optimize query; split job by tenant batch</td></tr>
          <tr><td>Job count</td><td>&gt; 20 concurrent jobs</td><td>Externalize scheduler to dedicated worker pod</td></tr>
          <tr><td>Missed runs</td><td>≥ 2/week</td><td>Increase <code>misfire_grace_time</code> + move to externalized scheduler</td></tr>
        </tbody>
      </table>

      <h2>Headroom check cadence</h2>
      <p>Monthly headroom review (every 1st Tuesday): Founder reads the Atlas + Sentry dashboards, files a "headroom report" in <code>capacity_reports</code>. If any metric is within 25% of its trigger, we move BEFORE the trigger fires.</p>

      <h2>Forecasting</h2>
      <p>CARTIE's own <a href="/admin/roadmap-forecast">Roadmap-Aware Forecast</a> is used internally to predict cost (and therefore capacity) as customer count grows. The Jira automation that imports product-launch events also fires capacity-review tickets when projected MAU growth exceeds 50%.</p>

      <h2>Sign-off</h2>
      <p>Runbook approved Feb 18, 2026. Next review: May 18, 2026.</p>
    </LegalDoc>
  );
}
